import { useState, useEffect } from "react";

// Hook to generate a thumbnail from a video at a specific time
export const useVideoThumbnail = (
  videoUrl: string | null | undefined,
  targetSecond: number = 1 // Default to 1 second if not provided
) => {
  const [thumbnail, setThumbnail] = useState<string | null>(null);

  useEffect(() => {
    if (!videoUrl) return;

    let video: HTMLVideoElement | null = null;
    let isCleanedUp = false;

    const generateThumbnail = async () => {
      try {
        video = document.createElement("video");

        video.crossOrigin = "anonymous"; // I want to access this video from another domain, but don't send cookies or credentials.
        video.preload = "metadata"; // Just upload video information (duration, dimensions). We don't need to download the entire video, just a single frame.
        video.muted = true; // Firefox requires muted for autoplay
        video.playsInline = true;

        // Create a URL blob if necessary (for better compatibility)
        const videoSource = videoUrl;

        // Handle the metadata loaded event
        const handleLoadedMetadata = () => {
          if (isCleanedUp || !video) return;

          // Calculate the actual second to use
          // If targetSecond is greater than video duration, use the last second
          // Math.max with 0 ensures we don't get negative values for very short videos
          const actualSecond = Math.min(
            targetSecond,
            Math.max(0, video.duration - 0.1) // Subtract 0.1 to avoid potential end-of-video issues
          );

          const handleSeeked = () => {
            if (isCleanedUp || !video) return;

            // A small delay (100ms) is added after the seeked event to ensure the frame is rendered.
            setTimeout(() => {
              if (isCleanedUp || !video) return;

              const canvas = document.createElement("canvas");
              canvas.width = video.videoWidth; //  video.videoWidth (video resolution) vs video.width (HTML element size)
              canvas.height = video.videoHeight;

              const ctx = canvas.getContext("2d");
              if (ctx) {
                ctx.drawImage(video, 0, 0, canvas.width, canvas.height); // drawImage Takes the current frame of the video => Draws it on the canvas at coordinates (0, 0) => Scales it to the canvas size

                // Try to generate the thumbnail
                try {
                  const thumbnailUrl = canvas.toDataURL(
                    // encodes the image as text
                    "image/jpeg", // "image/jpeg": Output format (can also be "image/png").
                    0.8 // Quality (0 = worst, 1 = best). 0.8 is a good balance between quality and size.
                  );
                  if (!isCleanedUp) {
                    setThumbnail(thumbnailUrl);
                  }
                } catch (error) {
                  console.error("Error generating thumbnail:", error);
                }
              }

              // Clean up after generating the thumbnail
              if (video) {
                video.pause();
                video.removeAttribute("src");
                video.load();
              }
            }, 100);
          };

          // Seek = jump to a specific moment in the video
          // We need this event because seeking is asynchronous—it's not instantaneous. The browser needs to: Download the frames around the 1st second, Decode the video, and Prepare the frame for display.
          // The seeked event tells us: "I'm done jumping to the second you requested."
          video.addEventListener("seeked", handleSeeked, { once: true });

          // Set the time after the metadata is loaded
          // "seek at the calculated actualSecond
          video.currentTime = actualSecond;
        };

        const handleError = (error: Event) => {
          console.error("Video loading error:", error);

          // Try a different time if there is an error
          // Use a fallback time that's 10% into the video or 0.1 seconds, whichever is smaller
          if (video && video.duration > 0) {
            video.currentTime = Math.min(0.1, video.duration * 0.1);
          }
        };

        // loadedmetadata is an event that fires when the browser has loaded the video metadata
        video.addEventListener("loadedmetadata", handleLoadedMetadata, {
          once: true, // The event is only executed once and then automatically removed, It's like making removeEventListener automatically after the first execution.
        });
        video.addEventListener("error", handleError, { once: true });

        // Set the video source
        video.src = videoSource;

        // For Firefox, it is important to explicitly load
        video.load();
      } catch (error) {
        console.error("Error in thumbnail generation:", error);
      }
    };

    generateThumbnail();

    // Cleanup function
    return () => {
      isCleanedUp = true;
      if (video) {
        video.pause();
        video.removeAttribute("src");
        video.load();
        video = null;
      }
      setThumbnail(null);
    };
  }, [videoUrl, targetSecond]);

  return thumbnail;
};
